import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Globe, User, KeyRound, ShieldCheck, Copy, ExternalLink, X, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

// Clipboard-handoff "Quick Launch" for a website credential (Cmd+K → 🌐 Login).
// Browsers can't autofill a third-party origin, so we open the site and hand the
// agent copy buttons for username / password / live 2FA code. Self-contained:
// fetches from /vault/entries/:id/web-login, and if the vault is locked it shows
// an inline PIN prompt (reusing the 30-min vault session) and retries.
//
// Centered portal modal per the CLAUDE.md modal rule (escapes transformed
// ancestors; sits above the Cmd+K palette at zIndex 10000).
export default function WebLoginHelper({ entryId, onClose }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needPin, setNeedPin] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const clipboardClearRef = useRef(null);

  const fetchLogin = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.post(`/vault/entries/${entryId}/web-login`);
      setData(res.data);
      setNeedPin(false);
      if (res.data?.totp?.time_remaining != null) setRemaining(res.data.totp.time_remaining);
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === 'VAULT_REQUIRED' || code === 'VAULT_EXPIRED') {
        setNeedPin(true);
      } else {
        toast.error(err?.response?.data?.error || t('cmdk.webLogin.failed'));
        onClose();
      }
    } finally {
      setLoading(false);
    }
  }, [entryId, onClose, t]);

  useEffect(() => { fetchLogin(); }, [fetchLogin]);

  // Clean up a pending clipboard-clear timer on unmount.
  useEffect(() => () => { if (clipboardClearRef.current) clearTimeout(clipboardClearRef.current); }, []);

  // 2FA countdown; when it hits 0 refresh the live code (secret stays server-side).
  useEffect(() => {
    if (!data?.totp || remaining == null) return undefined;
    const id = setInterval(() => {
      setRemaining(prev => {
        if (prev == null) return prev;
        if (prev <= 1) {
          api.post(`/vault/entries/${entryId}/linked-2fa/reveal`)
            .then(({ data: d }) => {
              setData(cur => (cur ? { ...cur, totp: { ...cur.totp, current_code: d.current_code } } : cur));
            })
            .catch(() => { /* keep showing the stale code rather than erroring */ });
          return data.totp.period || 30;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [data, remaining, entryId]);

  const submitPin = async (e) => {
    e.preventDefault();
    if (!pin || pinBusy) return;
    setPinBusy(true);
    setPinError(false);
    try {
      const res = await api.post('/vault/pin/verify', { pin });
      sessionStorage.setItem('vaultToken', res.data.vaultToken);
      setPin('');
      await fetchLogin();
    } catch {
      setPinError(true);
    } finally {
      setPinBusy(false);
    }
  };

  const copy = async (value, label, isPassword) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t('cmdk.webLogin.copied', { what: label }));
      if (isPassword) {
        // Best-effort: wipe the clipboard after a minute, like a password manager.
        if (clipboardClearRef.current) clearTimeout(clipboardClearRef.current);
        clipboardClearRef.current = setTimeout(() => {
          navigator.clipboard.writeText('').catch(() => {});
        }, 60000);
      }
    } catch {
      toast.error(t('common.actionFailed'));
    }
  };

  const openSite = () => {
    if (!data?.url) return;
    const href = /^https?:\/\//i.test(data.url) ? data.url : `https://${data.url}`;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const Row = ({ icon, label, value, onCopy, mono }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--c-border)' }}>
      <span style={{ color: 'var(--c-text-muted)', flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>{label}</div>
        <div style={{ fontSize: 14, color: 'var(--c-text)', fontFamily: mono ? 'monospace' : 'inherit', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value}
        </div>
      </div>
      {onCopy && (
        <button className="btn btn-secondary btn-sm" onClick={onCopy} style={{ flexShrink: 0 }}>
          <Copy size={14} /> {t('common.copy')}
        </button>
      )}
    </div>
  );

  const modal = (
    <div
      onMouseDown={e => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.55)', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ margin: 0, maxWidth: 440, width: '100%', maxHeight: '85vh', overflowY: 'auto' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Globe size={18} style={{ color: 'var(--c-brand)' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {data?.entry_name || t('cmdk.webLogin.title')}
            </div>
            {data?.company_name && <div style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>{data.company_name}</div>}
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose} aria-label={t('common.close')}>
            <X size={16} />
          </button>
        </div>

        {loading && <div style={{ padding: 20, textAlign: 'center', color: 'var(--c-text-muted)' }}>{t('common.loading')}</div>}

        {!loading && needPin && (
          <form onSubmit={submitPin}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, color: 'var(--c-text-muted)', fontSize: 13 }}>
              <Lock size={15} /> {t('cmdk.webLogin.pinPrompt')}
            </div>
            <div className="form-group">
              <input
                type="password"
                inputMode="numeric"
                autoFocus
                className="form-input"
                value={pin}
                onChange={(e) => { setPin(e.target.value); setPinError(false); }}
                placeholder={t('cmdk.webLogin.pinPlaceholder')}
              />
              {pinError && <div style={{ color: 'var(--c-danger, #ef4444)', fontSize: 12, marginTop: 4 }}>{t('cmdk.webLogin.pinInvalid')}</div>}
            </div>
            <button type="submit" className="btn btn-primary" disabled={pinBusy || !pin} style={{ width: '100%' }}>
              {pinBusy ? t('common.loading') : t('cmdk.webLogin.unlock')}
            </button>
          </form>
        )}

        {!loading && !needPin && data && (
          <>
            <div>
              {data.url && (
                <Row icon={<Globe size={16} />} label={t('cmdk.webLogin.site')} value={data.url} />
              )}
              {data.username && (
                <Row icon={<User size={16} />} label={t('cmdk.webLogin.username')} value={data.username}
                  onCopy={() => copy(data.username, t('cmdk.webLogin.username'))} />
              )}
              {data.password && (
                <Row icon={<KeyRound size={16} />} label={t('cmdk.webLogin.password')} value={'••••••••••'}
                  onCopy={() => copy(data.password, t('cmdk.webLogin.password'), true)} />
              )}
              {data.totp?.current_code && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--c-border)' }}>
                  <span style={{ color: 'var(--c-text-muted)', flexShrink: 0 }}><ShieldCheck size={16} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
                      {t('cmdk.webLogin.twoFa')}{remaining != null ? ` · ${remaining}s` : ''}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'monospace', letterSpacing: 2, color: 'var(--c-text)' }}>
                      {data.totp.current_code}
                    </div>
                  </div>
                  <button className="btn btn-secondary btn-sm" onClick={() => copy(data.totp.current_code, t('cmdk.webLogin.twoFa'))} style={{ flexShrink: 0 }}>
                    <Copy size={14} /> {t('common.copy')}
                  </button>
                </div>
              )}
            </div>

            {!data.password && !data.username && (
              <div style={{ padding: '10px 0', color: 'var(--c-text-muted)', fontSize: 13 }}>
                {t('cmdk.webLogin.noCreds')}
              </div>
            )}

            {data.url && (
              <button className="btn btn-primary" onClick={openSite} style={{ width: '100%', marginTop: 14 }}>
                <ExternalLink size={15} /> {t('cmdk.webLogin.openSite')}
              </button>
            )}
            <div style={{ fontSize: 11, color: 'var(--c-text-muted)', textAlign: 'center', marginTop: 8 }}>
              {t('cmdk.webLogin.hint')}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
